import { collection, getDocs, limit, query, where } from 'firebase/firestore';

import { OutletDirectorySchema, type OutletDirectoryRow } from '@kilat/shared';

import { db } from '../firebase.js';
import { logger } from '../logger.js';

/**
 * Public directory read for the agent map (feature: map-dashboards).
 *
 * The agent's `agents/{agentId}/operators/{tenantId}` back-refs carry no
 * outlet coordinates (agents can't read `tenants/**` cross-tenant), so the
 * only geo source available to an agent is the PUBLIC find directory. The
 * agent map joins its back-refs to these rows by `tenantId`.
 *
 * The `listedInFindDirectory == true` filter is REQUIRED by
 * `infra/firestore.rules` — that rule is exactly what makes a non-super-admin
 * read allowed, so a whole-collection read would be denied. Equality-only +
 * limit is served by the merged single-field index (no composite index, no
 * rules change).
 */

/** Cost cap: bounds the one-shot read to the free-tier budget. */
const LISTED_CAP = 200;

export async function listListedOutlets(): Promise<OutletDirectoryRow[]> {
  const snap = await getDocs(
    query(
      collection(db, 'outletDirectory'),
      where('listedInFindDirectory', '==', true),
      limit(LISTED_CAP),
    ),
  );
  const out: OutletDirectoryRow[] = [];
  for (const d of snap.docs) {
    const parsed = OutletDirectorySchema.safeParse(d.data());
    if (parsed.success) {
      // id attached post-parse so a schema without an `id` field can't strip it.
      out.push({ ...parsed.data, id: d.id });
    } else {
      logger.warn('agent map: directory row parse failed', { id: d.id });
    }
  }
  return out;
}
